import React from 'react'
import ReactDOM from 'react-dom'
import styled, { css } from 'styled-components'

import { Title } from '../Title'

import { useFocus } from './useFocus'
import { useModalState } from './useModalState'

interface Props {
  disableFocusOnModalComponent?: boolean
  title?: string
  onCloseRequest?: () => void
  size?: ModalSize
  className?: string
  hasOuterScroll?: boolean
  compact?: boolean
}

type ModalSize = 'xl' | 'lg' | 'md'

export const Modal: React.FC<Props> = ({
  children,
  disableFocusOnModalComponent,
  title,
  onCloseRequest,
  size,
  className,
  hasOuterScroll,
  compact,
}) => {
  const { handleClose, isTransitioning, transitionDurationMs } = useModalState(
    300,
    onCloseRequest
  )
  const elementToFocus = useFocus<HTMLDivElement>()

  return ReactDOM.createPortal(
    <Wrapper
      isTransitioning={isTransitioning}
      transitionDurationMs={transitionDurationMs}
      ref={!disableFocusOnModalComponent ? elementToFocus : undefined}
      role='dialog'
      tabIndex={0}
      onKeyDown={e => {
        if (e.keyCode === 27) {
          handleClose(e)
        }
      }}
    >
      <Backdrop onClick={handleClose} />
      <ModalContainer hasOuterScroll={hasOuterScroll} onClick={handleClose}>
        <ModalDialog
          data-role='modal'
          size={size || 'md'}
          onClick={e => {
            e.stopPropagation()
          }}
          className={className}
          hasOuterScroll={hasOuterScroll}
          compact={compact}
        >
          {!compact ? (
            <ModalHeader hasSeparator={!!title}>
              {title ? <Title size={4}>{title}</Title> : <div />}
              <ButtonClose onClick={handleClose}>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 32 32'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    fillRule='evenodd'
                    clipRule='evenodd'
                    d='M18.1814 16L31.7569 29.5756L29.5751 31.7574L15.9995 18.1819L2.42401 31.7574L0.242188 29.5756L13.8177 16L0.242188 2.42449L2.42401 0.242676L15.9995 13.8182L29.5751 0.242676L31.7569 2.42449L18.1814 16Z'
                    fill='#0E1A2F'
                  />
                </svg>
              </ButtonClose>
            </ModalHeader>
          ) : null}
          <ModalBody hasOuterScroll={hasOuterScroll}>{children}</ModalBody>
        </ModalDialog>
      </ModalContainer>
    </Wrapper>,
    document.body
  )
}

const Backdrop = styled.div`
  display: block;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
`

interface WrapperProps {
  isTransitioning: boolean
  transitionDurationMs: number
}
const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;

  /* TRANSITION EFFECT */
  transition-duration: ${({ transitionDurationMs }) =>
    transitionDurationMs / 1000}s;
  transition-property: opacity;
  transition-timing-function: ease-in-out;
  opacity: ${({ isTransitioning }) => (isTransitioning ? 1 : 0)};
`

interface ModalContainerProps {
  hasOuterScroll?: boolean
}

const ModalContainer = styled.div<ModalContainerProps>`
  ${({ hasOuterScroll }) =>
    hasOuterScroll
      ? css`
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          overflow: scroll;
          padding-top: 3rem;
        `
      : null}
`

interface ModalDialogProps {
  size?: ModalSize
  hasOuterScroll?: boolean
  compact?: boolean
}

const ModalDialog = styled.div<ModalDialogProps>`
  display: block;
  padding: ${({ compact }) => (compact ? '0' : '2rem')};
  background-color: #fff;
  width: ${({ size }) =>
    size === 'xl' ? '1150px' : size === 'lg' ? '900px' : '600px'};
  border-radius: 20px;
  box-shadow: ${({ theme }) => theme.shadow.espansa};
  max-width: 90%;
  position: absolute;

  ${({ hasOuterScroll }) =>
    hasOuterScroll
      ? css`
          top: 10%;
          left: 50%;
          transform: translate(-50%, 0);
          margin-bottom: 3rem;
        `
      : css`
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        `}
`

interface ModalHeaderProps {
  hasSeparator?: boolean
}

const ModalHeader = styled.div<ModalHeaderProps>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;

  ${({ hasSeparator }) =>
    hasSeparator
      ? css`
          padding-bottom: 1rem;
          border-bottom: 1px solid ${({ theme }) => theme.color.neutri.grigio};
        `
      : null}
`

interface ModalBodyProps {
  hasOuterScroll?: boolean
}

const ModalBody = styled.div<ModalBodyProps>`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  overflow: auto;

  ${({ hasOuterScroll }) =>
    hasOuterScroll
      ? css``
      : css`
          max-height: 200px;
          @media (min-height: 450px) {
            max-height: 300px;
          }
          @media (min-height: 600px) {
            max-height: 450px;
          }
          @media (min-height: 700px) {
            max-height: 550px;
          }
          @media (min-height: 900px) {
            max-height: 700px;
          }
        `}
`

const ButtonClose = styled.button`
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
`
